import { useEffect, useMemo, useRef, useState } from "react";

// Heart-shaped mosaic backdrop.
// Performance-optimized: the grid renders once, and the animation loop
// mutates DOM styles directly via refs instead of triggering React re-renders
// every frame. Visual effects are identical to the previous version.

const CELL_SIZE = 68; // px — size of each mosaic tile

const SHAPE_INSETS: [number, number][] = [
  [0, 0],
  [1, 1],
  [2, 2],
  [3, 3],
  [4, 4],
  [5, 5],
  [6, 6],
];

const PATTERN_ROWS = SHAPE_INSETS.length;

const WHITE_TINTS = [
  "rgba(255, 255, 255, 0.35)",
  "rgba(255, 255, 255, 0.45)",
  "rgba(255, 255, 255, 0.55)",
  "rgba(255, 255, 255, 0.65)",
  "rgba(255, 255, 255, 0.75)",
];

const GRADIENT_STOPS: [number, [number, number, number]][] = [
  [0.0, [255, 200, 221]],
  [0.22, [255, 214, 218]],
  [0.4, [230, 205, 245]],
  [0.58, [205, 220, 255]],
  [0.78, [195, 235, 240]],
  [1.0, [220, 240, 220]],
];

const MACARON_COLORS = [
  "#FFB5C5",
  "#FFD6A5",
  "#FDFFB6",
  "#CAFFBF",
  "#9BF6FF",
  "#A0C4FF",
  "#BDB2FF",
  "#FFC6FF",
  "#FFCFD2",
  "#B5EAD7",
  "#F1C0E8",
  "#C1FBA4",
];

const pickMacaron = (r: number, c: number, offset = 0) => {
  const seed =
    (r * 31 + c * 17 + r * c * 7 + offset * 13) % MACARON_COLORS.length;
  return MACARON_COLORS[seed];
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const sampleGradient = (t: number): [number, number, number] => {
  const clamped = Math.max(0, Math.min(1, t));
  for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
    const [p1, c1] = GRADIENT_STOPS[i];
    const [p2, c2] = GRADIENT_STOPS[i + 1];
    if (clamped >= p1 && clamped <= p2) {
      const local = (clamped - p1) / (p2 - p1);
      return [
        Math.round(lerp(c1[0], c2[0], local)),
        Math.round(lerp(c1[1], c2[1], local)),
        Math.round(lerp(c1[2], c2[2], local)),
      ];
    }
  }
  return GRADIENT_STOPS[GRADIENT_STOPS.length - 1][1];
};

const pickWhiteTint = (r: number, c: number) => {
  const seed = (r * 41 + c * 23 + r * c * 3) % WHITE_TINTS.length;
  return WHITE_TINTS[seed];
};

type TrailPoint = { x: number; y: number; strength: number };

type CellRefs = {
  root: HTMLDivElement | null;
  resting: HTMLDivElement | null;
  hover: HTMLDivElement | null;
  shimmer: HTMLDivElement | null;
  sparkle: HTMLDivElement | null;
};

const MAX_TRAIL = 10;
const REVEAL_RADIUS = 220;

export default function HeroBackground() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const trailRef = useRef<TrailPoint[]>([]);
  const auraRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cellRefs = useRef<Map<string, CellRefs>>(new Map());
  // Track which cells are currently "active" (reveal > 0) so we can
  // reset only those cells on the next frame instead of touching all cells.
  const activeCellsRef = useRef<Set<string>>(new Set());
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalCols = Math.max(12, Math.ceil(size.w / CELL_SIZE));
  const totalRows = PATTERN_ROWS;

  const patternAt = (r: number, c: number): number => {
    if (r < 0 || r >= PATTERN_ROWS) return 0;
    const [leftInset, rightInset] = SHAPE_INSETS[r];
    if (leftInset < 0) return 0;
    if (c < leftInset) return 0;
    if (c > totalCols - 1 - rightInset) return 0;
    return 1;
  };

  const cellConfig = useMemo(() => {
    const config: Record<
      string,
      {
        whiteTint: string;
        hoverGradient: string;
        restingColor: string;
        restingOpacity: number;
        centerBoost: number;
      }
    > = {};
    const centerC = (totalCols - 1) / 2;
    const centerR = PATTERN_ROWS * 0.55;
    const radiusC = totalCols * 0.3;
    const radiusR = PATTERN_ROWS * 0.7;

    for (let r = 0; r < PATTERN_ROWS; r++) {
      for (let c = 0; c < totalCols; c++) {
        if (patternAt(r, c) === 0) continue;

        const t =
          (c / Math.max(1, totalCols - 1)) * 0.6 +
          (r / Math.max(1, PATTERN_ROWS - 1)) * 0.4;
        const [rr, gg, bb] = sampleGradient(t);
        const restingColor = `rgb(${rr}, ${gg}, ${bb})`;

        const m1 = pickMacaron(r, c, 0);
        const m2 = pickMacaron(r, c, 3);
        const m3 = pickMacaron(r, c, 7);
        const hoverGradient = `linear-gradient(135deg, ${m1} 0%, ${m2} 55%, ${m3} 100%)`;

        const dr = (r - centerR) / radiusR;
        const dc = (c - centerC) / radiusC;
        const dist = Math.sqrt(dr * dr + dc * dc);
        const raw = Math.max(0, 1 - dist);
        const centerBoost = 1 - Math.pow(1 - raw, 2);

        const hash = (r * 73 + c * 131 + r * c * 11) % 5;
        const restingOpacity = 0.42 + centerBoost * 0.32 + hash * 0.015;

        const baseWhite = pickWhiteTint(r, c);
        const whiteBoostIdx = Math.min(
          WHITE_TINTS.length - 1,
          WHITE_TINTS.indexOf(baseWhite) + Math.round(centerBoost * 2)
        );
        const whiteTint = WHITE_TINTS[whiteBoostIdx];

        config[`${r}-${c}`] = {
          whiteTint,
          hoverGradient,
          restingColor,
          restingOpacity,
          centerBoost,
        };
      }
    }
    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCols]);

  // Track mouse globally — writes to a ref, does NOT trigger a render.
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pad = 160;
      if (
        e.clientX < rect.left - pad ||
        e.clientX > rect.right + pad ||
        e.clientY < rect.top - pad ||
        e.clientY > rect.bottom + pad
      ) {
        mouseRef.current = null;
        return;
      }
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    window.addEventListener("mousemove", handle, { passive: true });
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  // Animation loop — drives everything via direct DOM writes, no React state.
  useEffect(() => {
    if (size.w === 0 || size.h === 0) return;

    const cellW = size.w / totalCols;
    const cellH = size.h / totalRows;

    let raf = 0;
    let mounted = true;

    const loop = () => {
      if (!mounted) return;

      // 1) Decay trail in-place (avoid allocating a new array every frame)
      const trail = trailRef.current;
      let write = 0;
      for (let i = 0; i < trail.length; i++) {
        const p = trail[i];
        p.strength *= 0.9;
        if (p.strength > 0.04) {
          trail[write++] = p;
        }
      }
      trail.length = write;

      // 2) Append current mouse pos as a new trail head if moved enough
      const head = mouseRef.current;
      if (head) {
        const last = trail[trail.length - 1];
        const distFromLast = last
          ? Math.hypot(last.x - head.x, last.y - head.y)
          : Infinity;
        if (!last || distFromLast > 18) {
          trail.push({ x: head.x, y: head.y, strength: 1 });
          if (trail.length > MAX_TRAIL) trail.shift();
        } else {
          last.strength = Math.min(1, last.strength + 0.15);
        }
      }

      // 3) Update aura layer via refs (no re-render)
      for (let i = 0; i < MAX_TRAIL; i++) {
        const el = auraRefs.current[i];
        if (!el) continue;
        const p = trail[i];
        if (!p) {
          if (el.style.display !== "none") el.style.display = "none";
          continue;
        }
        if (el.style.display === "none") el.style.display = "block";
        el.style.transform = `translate3d(${p.x - 200}px, ${p.y - 200}px, 0)`;
        // Update gradient stops via CSS custom properties for cheaper updates
        el.style.background = `radial-gradient(circle, rgba(255,182,193,${
          0.45 * p.strength
        }), rgba(189,178,255,${0.3 * p.strength}) 40%, rgba(155,246,255,${
          0.2 * p.strength
        }) 65%, rgba(255,255,255,0) 82%)`;
      }

      // 4) Determine which cells are potentially affected — only those
      // inside the bounding box of any trail point (plus radius).
      const nextActive = new Set<string>();
      if (trail.length > 0 && cellW > 0 && cellH > 0) {
        for (let ti = 0; ti < trail.length; ti++) {
          const p = trail[ti];
          const minC = Math.max(
            0,
            Math.floor((p.x - REVEAL_RADIUS) / cellW)
          );
          const maxC = Math.min(
            totalCols - 1,
            Math.ceil((p.x + REVEAL_RADIUS) / cellW)
          );
          const minR = Math.max(
            0,
            Math.floor((p.y - REVEAL_RADIUS) / cellH)
          );
          const maxR = Math.min(
            totalRows - 1,
            Math.ceil((p.y + REVEAL_RADIUS) / cellH)
          );
          for (let r = minR; r <= maxR; r++) {
            for (let c = minC; c <= maxC; c++) {
              if (patternAt(r, c) === 0) continue;
              nextActive.add(`${r}-${c}`);
            }
          }
        }
      }

      // 5) Reset cells that were active last frame but not this frame
      const prevActive = activeCellsRef.current;
      prevActive.forEach((key) => {
        if (nextActive.has(key)) return;
        const refs = cellRefs.current.get(key);
        if (!refs) return;
        const cfg = cellConfig[key];
        if (!cfg) return;
        if (refs.root) {
          refs.root.style.transform =
            "translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) scale(1)";
          refs.root.style.boxShadow =
            cfg.centerBoost > 0.55
              ? `inset 0 0 ${
                  12 + cfg.centerBoost * 14
                }px rgba(255,255,255,${0.35 * cfg.centerBoost})`
              : "";
          refs.root.style.zIndex = "1";
        }
        if (refs.resting) refs.resting.style.opacity = `${cfg.restingOpacity}`;
        if (refs.hover) refs.hover.style.opacity = "0";
        if (refs.shimmer) {
          refs.shimmer.style.opacity = "0";
          refs.shimmer.style.transform = "translateX(-30%)";
        }
        if (refs.sparkle) refs.sparkle.style.opacity = "0";
      });

      // 6) Apply reveal for the currently active cells
      nextActive.forEach((key) => {
        const refs = cellRefs.current.get(key);
        if (!refs) return;
        const cfg = cellConfig[key];
        if (!cfg) return;

        const [rStr, cStr] = key.split("-");
        const rIdx = +rStr;
        const cIdx = +cStr;
        const cx = (cIdx + 0.5) * cellW;
        const cy = (rIdx + 0.5) * cellH;

        // Sum reveal from every trail point (with decayed strength)
        let reveal = 0;
        for (let i = 0; i < trail.length; i++) {
          const p = trail[i];
          const dx = p.x - cx;
          const dy = p.y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          let rr = Math.max(0, 1 - dist / REVEAL_RADIUS);
          rr = 1 - Math.pow(1 - rr, 3);
          const r = rr * p.strength;
          if (r > reveal) reveal = r;
        }

        // Magnetic tilt toward the current cursor head
        let tiltX = 0;
        let tiltY = 0;
        let liftZ = 0;
        if (head && reveal > 0.02) {
          const dx = head.x - cx;
          const dy = head.y - cy;
          const norm = Math.min(1, Math.hypot(dx, dy) / 200);
          const strength = reveal * (1 - norm);
          tiltY = (dx / 200) * strength * 14;
          tiltX = (-dy / 200) * strength * 14;
          liftZ = strength * 18;
        }

        const scale = 1 + reveal * 0.09;
        const lift = -reveal * 3;

        if (refs.root) {
          refs.root.style.transform = `translate3d(0, ${lift}px, ${liftZ}px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${scale})`;
          refs.root.style.zIndex = reveal > 0.3 ? "2" : "1";
          refs.root.style.boxShadow =
            reveal > 0.15
              ? `0 ${4 + reveal * 10}px ${8 + reveal * 22}px -${
                  4 + reveal * 4
                }px rgba(189,178,255,${0.28 * reveal}), 0 0 ${
                  12 + reveal * 20
                }px rgba(255,182,193,${0.22 * reveal}), inset 0 0 ${
                  12 + cfg.centerBoost * 14
                }px rgba(255,255,255,${
                  0.35 * Math.max(cfg.centerBoost, reveal)
                })`
              : cfg.centerBoost > 0.55
                ? `inset 0 0 ${
                    12 + cfg.centerBoost * 14
                  }px rgba(255,255,255,${0.35 * cfg.centerBoost})`
                : "";
        }
        if (refs.resting) {
          refs.resting.style.opacity = `${
            cfg.restingOpacity * (1 - reveal * 0.55)
          }`;
        }
        if (refs.hover) refs.hover.style.opacity = `${reveal}`;
        if (refs.shimmer) {
          refs.shimmer.style.opacity = `${reveal * 0.55}`;
          refs.shimmer.style.transform = `translateX(${(reveal - 0.5) * 60}%)`;
        }
        if (refs.sparkle) refs.sparkle.style.opacity = `${reveal * 0.9}`;
      });

      activeCellsRef.current = nextActive;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.w, size.h, totalCols, cellConfig]);

  const cellW = totalCols > 0 ? size.w / totalCols : CELL_SIZE;
  const cellH = totalRows > 0 ? size.h / totalRows : CELL_SIZE;

  // Grid renders ONCE per size change — every frame after that mutates DOM directly.
  const gridCells = useMemo(() => {
    if (size.w === 0 || size.h === 0) return null;
    // Reset cell ref map when we rebuild the grid
    cellRefs.current = new Map();

    const cells: JSX.Element[] = [];
    for (let rIdx = 0; rIdx < totalRows; rIdx++) {
      for (let cIdx = 0; cIdx < totalCols; cIdx++) {
        const inside = patternAt(rIdx, cIdx) === 1;
        if (!inside) {
          cells.push(<div key={`${rIdx}-${cIdx}`} />);
          continue;
        }

        const key = `${rIdx}-${cIdx}`;
        const cfg = cellConfig[key];
        if (!cfg) {
          cells.push(<div key={key} />);
          continue;
        }

        cells.push(
          <div
            key={key}
            ref={(el) => {
              const existing = cellRefs.current.get(key) ?? {
                root: null,
                resting: null,
                hover: null,
                shimmer: null,
                sparkle: null,
              };
              existing.root = el;
              cellRefs.current.set(key, existing);
            }}
            className="relative overflow-hidden"
            style={{
              backgroundColor: cfg.whiteTint,
              border: "1px solid rgba(0,0,0,0.06)",
              transform:
                "translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) scale(1)",
              transformStyle: "preserve-3d",
              willChange: "transform, box-shadow",
              transition:
                "transform 380ms cubic-bezier(0.22, 1, 0.36, 1), background-color 380ms ease, box-shadow 380ms ease",
              boxShadow:
                cfg.centerBoost > 0.55
                  ? `inset 0 0 ${
                      12 + cfg.centerBoost * 14
                    }px rgba(255,255,255,${0.35 * cfg.centerBoost})`
                  : undefined,
              zIndex: 1,
            }}
          >
            <div
              ref={(el) => {
                const existing = cellRefs.current.get(key) ?? {
                  root: null,
                  resting: null,
                  hover: null,
                  shimmer: null,
                  sparkle: null,
                };
                existing.resting = el;
                cellRefs.current.set(key, existing);
              }}
              className="absolute inset-0"
              style={{
                backgroundColor: cfg.restingColor,
                opacity: cfg.restingOpacity,
                transition: "opacity 380ms ease",
                willChange: "opacity",
              }}
            />

            <div
              ref={(el) => {
                const existing = cellRefs.current.get(key) ?? {
                  root: null,
                  resting: null,
                  hover: null,
                  shimmer: null,
                  sparkle: null,
                };
                existing.hover = el;
                cellRefs.current.set(key, existing);
              }}
              className="absolute inset-0"
              style={{
                background: cfg.hoverGradient,
                opacity: 0,
                transition: "opacity 380ms ease",
                mixBlendMode: "screen",
                willChange: "opacity",
              }}
            />

            <div
              ref={(el) => {
                const existing = cellRefs.current.get(key) ?? {
                  root: null,
                  resting: null,
                  hover: null,
                  shimmer: null,
                  sparkle: null,
                };
                existing.shimmer = el;
                cellRefs.current.set(key, existing);
              }}
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.9) 50%, transparent 70%)",
                opacity: 0,
                transform: "translateX(-30%)",
                transition:
                  "opacity 380ms ease, transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                mixBlendMode: "screen",
                willChange: "opacity, transform",
              }}
            />

            <div
              ref={(el) => {
                const existing = cellRefs.current.get(key) ?? {
                  root: null,
                  resting: null,
                  hover: null,
                  shimmer: null,
                  sparkle: null,
                };
                existing.sparkle = el;
                cellRefs.current.set(key, existing);
              }}
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.7), rgba(255,255,255,0) 55%)",
                opacity: 0,
                transition: "opacity 380ms ease",
                willChange: "opacity",
              }}
            />
          </div>
        );
      }
    }
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.w, size.h, totalCols, cellConfig]);

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 overflow-hidden">
      <div ref={wrapRef} className="relative w-full h-[560px] overflow-hidden">
        {/* Layer 1: subtle grid lines */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(23,23,23,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(23,23,23,0.05) 1px, transparent 1px)",
            backgroundSize: `${cellW || CELL_SIZE}px ${cellH || CELL_SIZE}px`,
            backgroundPosition: "0 0",
          }}
        />

        {/* Layer 2: soft ambient neutral aura */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 45% 65% at 50% 40%, rgba(240,240,245,0.4) 0%, rgba(250,250,252,0.22) 45%, rgba(255,255,255,0) 78%)",
            filter: "blur(30px)",
          }}
        />

        {/* Layer 3: dreamy trailing macaron aura pool (mutated via refs) */}
        <div className="absolute inset-0" style={{ mixBlendMode: "screen" }}>
          {Array.from({ length: MAX_TRAIL }).map((_, i) => (
            <div
              key={`aura-${i}`}
              ref={(el) => {
                auraRefs.current[i] = el;
              }}
              className="absolute top-0 left-0"
              style={{
                width: 400,
                height: 400,
                display: "none",
                filter: "blur(30px)",
                willChange: "transform, background",
              }}
            />
          ))}
        </div>

        {/* Layer 4: mosaic tiles (rendered once, animated via refs) */}
        <div className="absolute inset-0" style={{ perspective: "800px" }}>
          {size.w > 0 && size.h > 0 && (
            <div
              className="grid gap-0 w-full h-full"
              style={{
                gridTemplateColumns: `repeat(${totalCols}, 1fr)`,
                gridTemplateRows: `repeat(${totalRows}, 1fr)`,
                transformStyle: "preserve-3d",
              }}
            >
              {gridCells}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}